import { textColorFields } from '../_shared/textColor.js';
import { animationFields, mediaRadiusField } from '../_shared/universal.js';
import { headingHighlightFields } from '../_shared/headingHighlight.js';
import { patternFields } from '../_shared/pattern.js';
import { textBlockSchema } from '../../blocks/text/index.js';
/**
 * collapsible-content — a FROM-SCRATCH, 1:1 rebuild of Shrine Pro's "Collapsible content" section
 * (shrine-pro-ref/sections/collapsible-content.liquid + blocks/collapsible-row-content.liquid, with the
 * accordion CSS ported VERBATIM from assets/base.css). It is the FAQ-style disclosure list: a heading over
 * a stack of `accordion-v2` rows, each a native `<details>`/`<summary>` (a Material Symbols icon + a title)
 * whose panel opens with Shrine's grid `0fr→1fr` animation — no JS. An optional side IMAGE turns the layout
 * into Shrine's 2-column grid.
 *
 * Field-type mapping (Shopify → ours): inline_richtext→richtext (bold / italic markup → highlight engines),
 * text→text, richtext→richtext, image_picker→image, `type: 'text' id: 'icon'`→icon-name (the Material
 * Symbols name — same engine as icon-bar / icons-with-content), color→color, header→group, visible_if→
 * visibleWhen. Ids are clean camelCase; labels/options/defaults stay faithful to Shrine (row_heading_size,
 * border_type/style/thickness, collapse_icon(_style/_radius), animation_speed, rows_spacing, image_ratio,
 * desktop_layout, the 6 color schemes + custom palette, the 13-pattern background).
 *
 * DELIBERATELY DEFERRED (faithful follow-ups, flagged — the theme-colors mode the seller's example uses is
 * reproduced 1:1): (a) Shrine's `enable_custom_style` per-state recolor suite (~18 controls: per open/closed
 * state border/header/icon/content colors + custom row/content padding, driving `accordion-v2--colors-custom`);
 * (b) `image_mobile`, a mobile-specific crop for the side image (the desktop image is used on mobile until then);
 * (c) the uploaded `video` slot for the side media — media upload is still a repo-wide stub (see spec 0020).
 */
/** Convenience for the literal hex defaults Shrine ships. */
const hex = (value) => ({ ref: 'custom', value });
const CONTENT = 'Content';
const ROWS = 'Rows';
const IMAGE = 'Image';
const COLOR = 'Color';
export const collapsibleContentSchema = {
    type: 'collapsible-content',
    name: 'Collapsible content',
    version: 1,
    templates: ['index', 'product', 'collection', 'page', 'thank-you'],
    fields: [
        // ── (top, ungrouped) ──────────────────────────────────────────────────────
        {
            id: 'displaySectionId',
            type: 'checkbox',
            label: 'Display section ID',
            default: false,
            info: 'Lets a button link scroll to this section.',
        },
        {
            id: 'visibility',
            type: 'select',
            label: 'Display on',
            default: 'always-display',
            options: [
                { value: 'always-display', label: 'All devices' },
                { value: 'mobile-hidden', label: 'Desktop only' },
                { value: 'desktop-hidden', label: 'Mobile only' },
            ],
        },
        // ── Content: caption + heading + highlight + size + alignment ─────────────────
        {
            id: 'caption',
            type: 'text',
            label: 'Caption',
            group: CONTENT,
        },
        {
            id: 'heading',
            type: 'richtext',
            label: 'Heading',
            group: CONTENT,
            default: { format: 'html', html: '<p>Collapsible content</p>' },
            info: 'Apply Bold (B) to a word for highlight 1, Italic (I) for highlight 2.',
        },
        {
            id: 'headingSize',
            type: 'select',
            label: 'Heading size',
            group: CONTENT,
            default: 'h1',
            options: [
                { value: 'h3', label: 'Extra small' },
                { value: 'h2', label: 'Small' },
                { value: 'h1', label: 'Medium' },
                { value: 'h0', label: 'Large' },
                { value: 'custom', label: 'Custom' },
            ],
        },
        {
            id: 'headingSizeDesktop',
            type: 'range',
            label: 'Custom desktop size',
            group: CONTENT,
            min: 24,
            max: 60,
            step: 3,
            unit: 'px',
            default: 39,
            visibleWhen: { field: 'headingSize', equals: 'custom' },
        },
        {
            id: 'headingSizeMobile',
            type: 'range',
            label: 'Custom mobile size',
            group: CONTENT,
            min: 20,
            max: 50,
            step: 2,
            unit: 'px',
            default: 28,
            visibleWhen: { field: 'headingSize', equals: 'custom' },
        },
        {
            id: 'headingAlignment',
            type: 'select',
            label: 'Heading alignment',
            group: CONTENT,
            default: 'center',
            options: [
                { value: 'left', label: 'Left' },
                { value: 'center', label: 'Center' },
                { value: 'right', label: 'Right' },
            ],
        },
        ...headingHighlightFields(1, CONTENT),
        ...headingHighlightFields(2, CONTENT),
        // ── Rows (accordion behavior) ────────────────────────────────────────────────
        {
            id: 'rowHeadingSize',
            type: 'select',
            label: 'Row heading size',
            group: ROWS,
            default: 'medium',
            options: [
                { value: 'small', label: 'Small' },
                { value: 'medium', label: 'Medium' },
                { value: 'large', label: 'Large' },
            ],
        },
        {
            // One switch for the whole list (founder request 2026-09-11: ten rows, no way to drop the leading
            // glyph). Off ⇒ no row shows its icon whatever the row says; on ⇒ each row decides (`showIcon`).
            id: 'showRowIcons',
            type: 'checkbox',
            label: 'Show row icons',
            group: ROWS,
            default: true,
        },
        {
            id: 'collapseIcon',
            type: 'select',
            label: 'Collapse icon',
            group: ROWS,
            default: 'caret',
            options: [
                { value: 'caret', label: 'Caret' },
                { value: 'plus', label: 'Plus' },
            ],
        },
        {
            id: 'collapseIconStyle',
            type: 'select',
            label: 'Collapse icon style',
            group: ROWS,
            default: 'standard',
            options: [
                { value: 'standard', label: 'Standard' },
                { value: 'solid', label: 'Solid' },
            ],
        },
        {
            id: 'collapseIconRadius',
            type: 'range',
            label: 'Collapse icon corner radius',
            group: ROWS,
            min: 0,
            max: 100,
            step: 5,
            unit: '%',
            default: 15,
            visibleWhen: { field: 'collapseIconStyle', equals: 'solid' },
        },
        {
            id: 'borderType',
            type: 'select',
            label: 'Border',
            group: ROWS,
            default: 'bottom',
            options: [
                { value: 'top-bottom', label: 'Top and bottom' },
                { value: 'bottom', label: 'Bottom' },
                { value: 'all', label: 'All sides' },
                { value: 'none', label: 'None' },
            ],
        },
        {
            id: 'borderStyle',
            type: 'select',
            label: 'Border style',
            group: ROWS,
            default: 'solid',
            options: [
                { value: 'solid', label: 'Solid' },
                { value: 'dashed', label: 'Dashed' },
            ],
            visibleWhen: { field: 'borderType', notEquals: 'none' },
        },
        {
            id: 'borderThickness',
            type: 'range',
            label: 'Border thickness',
            group: ROWS,
            min: 1,
            max: 6,
            step: 1,
            unit: 'px',
            default: 1,
            visibleWhen: { field: 'borderType', notEquals: 'none' },
        },
        {
            id: 'cornerRadius',
            type: 'range',
            label: 'Corner radius',
            group: ROWS,
            min: 0,
            max: 40,
            step: 1,
            unit: 'px',
            default: 8,
            visibleWhen: { field: 'borderType', equals: 'all' },
        },
        {
            id: 'rowsSpacing',
            type: 'range',
            label: 'Spacing between rows',
            group: ROWS,
            min: 0,
            max: 40,
            step: 2,
            unit: 'px',
            default: 4,
        },
        {
            id: 'animationSpeed',
            type: 'range',
            label: 'Open / close speed',
            group: ROWS,
            min: 0,
            max: 1,
            step: 0.1,
            unit: 's',
            default: 0.4,
        },
        // ── Image (optional side media → 2-column layout) ─────────────────────────────
        {
            id: 'image',
            type: 'image',
            label: 'Image',
            group: IMAGE,
            info: 'When set, the rows sit beside this image in a 2-column layout.',
        },
        {
            id: 'imageRatio',
            type: 'select',
            label: 'Image ratio',
            group: IMAGE,
            default: 'adapt',
            options: [
                { value: 'adapt', label: 'Adapt to image' },
                { value: 'small', label: 'Small' },
                { value: 'large', label: 'Large' },
            ],
        },
        {
            id: 'desktopLayout',
            type: 'select',
            label: 'Desktop image position',
            group: IMAGE,
            default: 'image_second',
            options: [
                { value: 'image_first', label: 'Image first' },
                { value: 'image_second', label: 'Image second' },
            ],
        },
        // ── Color: the 6 Shrine schemes + a custom palette (same engine as collage) ────
        {
            id: 'colorScheme',
            type: 'select',
            label: 'Color scheme',
            group: COLOR,
            default: 'background-1',
            options: [
                { value: 'accent-1', label: 'Accent 1' },
                { value: 'accent-2', label: 'Accent 2' },
                { value: 'background-1', label: 'Background 1' },
                { value: 'background-2', label: 'Background 2' },
                { value: 'inverse', label: 'Inverse' },
                { value: 'custom', label: 'Custom' },
            ],
        },
        {
            id: 'customBackground',
            type: 'color',
            label: 'Custom background color',
            group: COLOR,
            default: hex('#FFFFFF'),
            visibleWhen: { field: 'colorScheme', equals: 'custom' },
        },
        {
            id: 'customGradientBackground',
            type: 'gradient',
            label: 'Custom gradient background',
            group: COLOR,
            info: 'If set, replaces the background color.',
            visibleWhen: { field: 'colorScheme', equals: 'custom' },
        },
        {
            id: 'customImageBackground',
            type: 'image',
            label: 'Custom image background',
            group: COLOR,
            visibleWhen: { field: 'colorScheme', equals: 'custom' },
        },
        {
            id: 'customMobileImageBackground',
            type: 'image',
            label: 'Custom mobile image background',
            group: COLOR,
            info: 'If empty, the desktop image is used on mobile too.',
            visibleWhen: { field: 'colorScheme', equals: 'custom' },
        },
        {
            id: 'customText',
            type: 'color',
            label: 'Custom text color',
            group: COLOR,
            default: hex('#2E2A39'),
            visibleWhen: { field: 'colorScheme', equals: 'custom' },
        },
        // ── Background pattern (shared 13-pattern engine) ─────────────────────────────
        ...patternFields('Background pattern'),
        mediaRadiusField,
        // ── Section padding ───────────────────────────────────────────────────────────
        {
            id: 'paddingTop',
            type: 'range',
            label: 'Top padding',
            group: 'Section padding',
            min: 0,
            max: 100,
            step: 4,
            unit: 'px',
            default: 36,
        },
        {
            id: 'paddingBottom',
            type: 'range',
            label: 'Bottom padding',
            group: 'Section padding',
            min: 0,
            max: 100,
            step: 4,
            unit: 'px',
            default: 36,
        },
        // Text colors — the seller's own colour/gradient for this band's headings and body text.
        ...textColorFields(),
        // The universal Animation group (2026-09-18): this section writes its own section controls
        // instead of spreading `universalFields`, so the group is spread in on its own.
        ...animationFields,
    ],
    // Repeatable rows — Shrine's `collapsible-row-content` block.
    // Child types: the section's own rows + the global Text block (see `blocks` below).
    accepts: ['collapsible_row', 'text'],
    blocks: [
        {
            type: 'collapsible_row',
            name: 'Collapsible row',
            fields: [
                {
                    id: 'open',
                    type: 'checkbox',
                    label: 'Open row by default',
                    default: false,
                },
                {
                    id: 'heading',
                    type: 'text',
                    label: 'Heading',
                    default: 'Collapsible row',
                    defaultKey: 'section.collapsible_row',
                    // Placeholder de CONTENU, pas libellé d'interface — l'IA doit l'écrire (0059).
                    aiWritable: true,
                },
                {
                    // Per-row switch; the section's `showRowIcons` wins when it is off. An EMPTY icon name also hides
                    // the glyph (the renderer no longer falls back to `check_box` for an emptied field).
                    id: 'showIcon',
                    type: 'checkbox',
                    label: 'Show icon',
                    default: true,
                },
                {
                    id: 'icon',
                    type: 'icon-name',
                    label: 'Icon',
                    default: 'check_box',
                    placeholder: 'check_box',
                    info: 'A Google Material Symbols name — search one at fonts.google.com/icons.',
                    visibleWhen: { field: 'showIcon', equals: true },
                },
                {
                    id: 'filledIcon',
                    type: 'checkbox',
                    label: 'Filled icon',
                    default: false,
                    visibleWhen: { field: 'showIcon', equals: true },
                },
                {
                    id: 'customIcon',
                    type: 'image',
                    label: 'Custom icon',
                    visibleWhen: { field: 'showIcon', equals: true },
                    info: 'Overrides the Material Symbols icon above.',
                },
                {
                    id: 'content',
                    type: 'richtext',
                    label: 'Content',
                    default: { format: 'html', html: '<p>Row content…</p>' },
                },
            ],
        },
        // The global Text block (feature request 2026-09-11): a sub-title or a free paragraph the seller can
        // drop ANYWHERE among the rows — above the first, between two, after the last. Rendered in document
        // order by the section, through the shared block renderer.
        textBlockSchema,
    ],
    maxBlocks: 50,
};
export const collapsibleContentPresets = [
    {
        name: 'Collapsible content',
        settings: {
            heading: { format: 'html', html: '<p>Collapsible content</p>' },
            headingSize: 'h1',
            headingAlignment: 'center',
            colorScheme: 'background-1',
            borderType: 'bottom',
            collapseIcon: 'caret',
            rowHeadingSize: 'medium',
            paddingTop: 36,
            paddingBottom: 36,
        },
        blocks: [
            {
                type: 'collapsible_row',
                settings: {
                    open: true,
                    heading: '@t:cart.shipping',
                    content: {
                        format: 'html',
                        html: '<p>Votre commande est expédiée sous 24–48h. La livraison prend ensuite 2 à 5 jours ouvrés selon votre ville.</p>',
                    },
                },
            },
            {
                type: 'collapsible_row',
                settings: {
                    heading: '@t:checkout.pay_on_delivery',
                    content: {
                        format: 'html',
                        html: '<p>Vous réglez en espèces au livreur, à réception de votre colis. Aucun paiement en ligne n’est requis.</p>',
                    },
                },
            },
            {
                type: 'collapsible_row',
                settings: {
                    heading: '@t:section.returns_exchanges',
                    content: {
                        format: 'html',
                        html: '<p>Vous disposez de 14 jours pour un échange ou un retour. L’article doit être non porté, dans son état d’origine.</p>',
                    },
                },
            },
            {
                type: 'collapsible_row',
                settings: {
                    heading: '@t:section.need_help',
                    content: {
                        format: 'html',
                        html: '<p>Notre équipe vous répond du lundi au samedi. Contactez-nous et nous reviendrons vers vous rapidement.</p>',
                    },
                },
            },
        ],
    },
];
/**
 * How this section presents itself in "Add section": label, group, icon.
 *
 * It lives beside the schema, NOT in `index.ts`, and that is the point. `index.ts` is the module that pulls
 * in `Component.tsx`, and the builder reads `meta` for all sixty-two sections to paint the picker while
 * rendering only the handful a page actually uses. Keeping `meta` here lets `registry.lazy.gen.ts` import it
 * without dragging every section's render code into the editor's entry chunk.
 */
export const collapsibleContentMeta = {
    label: 'Collapsible content',
    group: 'Content',
    icon: 'chat',
};
//# sourceMappingURL=schema.js.map